import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.mark.msdiscoveries.com';

export default function OAuthCallback({ type = 'google' }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('connecting');
  const [message, setMessage] = useState('');
  const calledRef = useRef(false); // prevent double-invoke in React StrictMode

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const handleCallback = async () => {
      try {
        const code  = params.get('code');
        const state = params.get('state'); // userId encoded in state param

        if (!code || !state) {
          setStatus('error');
          setMessage('Missing OAuth code or state parameter');
          setTimeout(() => navigate('/client/email-accounts?error=oauth_failed'), 2500);
          return;
        }

        const endpoint = type === 'microsoft'
          ? `${API_BASE}/api/email-accounts/oauth/microsoft/exchange`
          : `${API_BASE}/api/email-accounts/oauth/google/exchange`;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, userId: state }),
        });

        // Handle non-JSON error responses (e.g. 502 Bad Gateway HTML pages)
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Server returned HTTP ${res.status} — ${res.statusText}`);
        }

        const data = await res.json();

        if (data.success) {
          setStatus('success');
          const successParam = type === 'microsoft' ? 'microsoft_connected' : 'gmail_connected';
          setTimeout(() => navigate(`/client/email-accounts?success=${successParam}`), 1500);
        } else {
          setStatus('error');
          setMessage(data.message || 'Connection failed');
          setTimeout(() => navigate('/client/email-accounts?error=oauth_failed'), 2500);
        }
      } catch (err) {
        console.error('[OAuthCallback] error:', err);
        setStatus('error');
        setMessage(err.message || 'Unexpected error occurred');
        setTimeout(() => navigate('/client/email-accounts?error=oauth_failed'), 2500);
      }
    };

    handleCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const providerName = type === 'microsoft' ? 'Microsoft' : 'Google';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A0A0A',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontFamily: 'system-ui, sans-serif',
    }}>
      {status === 'connecting' && (
        <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2ECC71', marginBottom: 8 }}>
            Connecting your {providerName} account...
          </div>
          <div style={{ fontSize: 13, color: '#555' }}>Please wait, do not close this page.</div>
        </>
      )}
      {status === 'success' && (
        <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2ECC71', marginBottom: 8 }}>
            {providerName} account connected!
          </div>
          <div style={{ fontSize: 13, color: '#555' }}>Redirecting you back...</div>
        </>
      )}
      {status === 'error' && (
        <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#E74C3C', marginBottom: 8 }}>
            Connection failed
          </div>
          <div style={{ fontSize: 13, color: '#aaa', maxWidth: 360, textAlign: 'center' }}>{message}</div>
          <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>Redirecting back in a moment...</div>
        </>
      )}
    </div>
  );
}
